import { getLocalesFromIsoLocale, IsoLocale, Locales, UsaIsoLocale } from "@simplyk/common";
import { useRouter } from "next/router";

import i18n from "../../../config/i18n";

import { useDonationFormTranslationField } from "@/hooks/useDonationTranslation";
import { DonationFormFrontendPageOutput } from "@/types/trpc";

export type DonationFormMetaContentType = "donationForm" | "p2p" | "team";

const getP2PHeaderMeta = ({
  locale,
  campaignTitle,
  orgName,
}: {
  locale: Locales;
  campaignTitle: string | undefined;
  orgName: string;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  switch (locale) {
    case Locales.EN:
      return {
        metaTitle: `Donate to ${campaignTitle} | ${orgName} | Zeffy`,
        metaDescription: `Help ${orgName} raise funds for ${campaignTitle}. Donate securely on Zeffy — 100% of your donation goes to the nonprofit.`,
      };
    case Locales.FR:
      return {
        metaTitle: `Faites un don à ${campaignTitle} | ${orgName} | Zeffy`,
        metaDescription: `Aidez ${orgName} à collecter des fonds pour ${campaignTitle}. Faites un don sécurisé sur Zeffy - 100 % de votre don est reversé à l'association.`,
      };
    case Locales.ES:
      return {
        metaTitle: `Dona a ${campaignTitle} | ${orgName} | Zeffy`,
        metaDescription: `Ayuda a ${orgName} a recaudar fondos para ${campaignTitle}. Dona de forma segura en Zeffy - el 100 % de tu donación se destina a la organización sin fines de lucro.`,
      };
  }
};

const getTeamHeaderMeta = ({
  locale,
  teamName,
  campaignTitle,
  orgName,
}: {
  locale: Locales;
  teamName: string | undefined;
  campaignTitle: string | undefined;
  orgName: string;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  switch (locale) {
    case Locales.EN:
      return {
        metaTitle: `Support ${teamName} for ${campaignTitle} | ${orgName} | Zeffy`,
        metaDescription: `${teamName} is fundraising for ${orgName}’s ${campaignTitle}. Make a donation on Zeffy and support their cause — 100% of your gift goes to the nonprofit.`,
      };
    case Locales.FR:
      return {
        metaTitle: `Soutenez ${teamName} pour ${campaignTitle} | ${orgName} | Zeffy`,
        metaDescription: `${teamName} collecte des fonds pour ${campaignTitle} de ${orgName}. Faites un don sur Zeffy et soutenez sa cause - 100 % de votre don est reversé à l'association à but non lucratif.`,
      };
    case Locales.ES:
      return {
        metaTitle: `Apoya a ${teamName} en ${campaignTitle} | ${orgName} | Zeffy`,
        metaDescription: `${teamName} está recaudando fondos para ${campaignTitle} de ${orgName}. Haz una donación en Zeffy y apoya su causa - el 100% de su donación se destina a la organización sin fines de lucro.`,
      };
  }
};

const getDonationFormHeaderMeta = ({
  locale,
  formTitle,
  orgName,
}: {
  locale: Locales;
  formTitle: string | undefined;
  orgName: string;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  switch (locale) {
    case Locales.EN:
      return {
        metaTitle: `Donate to ${orgName} | Zeffy`,
        metaDescription: `Help ${orgName} raise funds for ${formTitle}. Make a donation on Zeffy, and ensure 100% of your donation goes to fund ${orgName}’s mission.`,
      };

    case Locales.FR:
      return {
        metaTitle: `Faites un don à ${orgName} | Zeffy`,
        metaDescription: `Aidez ${orgName} à collecter des fonds pour ${formTitle}. Faites un don sur Zeffy et assurez-vous que 100 % de votre don soit consacré à la mission de ${orgName}.`,
      };

    case Locales.ES:
      return {
        metaTitle: `Dona a ${orgName} | Zeffy`,
        metaDescription: `Ayuda a ${orgName} a recaudar fondos para ${formTitle}. Haz una donación en Zeffy y asegúrate de que el 100% de tu donación se destine a la misión de ${orgName}.`,
      };
  }
};

export const useDonationFormMetaContent = ({
  donationForm,
  metaContentType,
  title,
}: {
  donationForm: DonationFormFrontendPageOutput;
  title: string | undefined;
  metaContentType: DonationFormMetaContentType;
}): {
  metaTitle: string | undefined;
  metaDescription: string | undefined;
} => {
  const router = useRouter();
  const { donationFormField: campaignFormField } = useDonationFormTranslationField(donationForm);

  const isoLocale = (i18n?.language || router.locale || UsaIsoLocale.EN) as IsoLocale;
  const locale = getLocalesFromIsoLocale(isoLocale);

  switch (metaContentType) {
    case "p2p":
      return getP2PHeaderMeta({ locale, campaignTitle: title, orgName: donationForm?.organization?.name });

    case "team":
      return getTeamHeaderMeta({
        locale,
        teamName: title,
        campaignTitle: campaignFormField?.title || donationForm?.organization?.name,
        orgName: donationForm?.organization?.name,
      });

    case "donationForm":
      return getDonationFormHeaderMeta({ locale, formTitle: title, orgName: donationForm?.organization?.name });
  }
};
